import { useCallback } from "react";
import { useNumericCell } from "../../hooks/useNumericCell";
import { useCellNavigation } from "../../utils/cellNavigation";
import { isPreOrderModifiable } from "../../services/recapService";
import type { FlowCellProps } from "./types";
import { CELL_INPUT } from "./styles";
import { cn } from "@/lib/utils";
import { AI_PENDING_CELL_CLASS } from "./AiChangeBadge";
import {
  AiCustomFieldBadge,
  isAiPendingCustomField,
} from "./AiCustomFieldBadge";

interface CustomColumnCellProps extends FlowCellProps {
  disabled?: boolean;
}

/**
 * Inline numeric editor for a flag-defined custom value column (e.g. Rolls,
 * Pallets). Reads/writes `flow.flow_metadata[key]`, where `key` comes from the
 * column def (`column.customColumnKey`). Behaves like `QuantityCell` minus the
 * junction-weight coupling — a plain per-row value summed in the footer.
 *
 * `FlowRow` routes any column carrying `customColumnKey` here, so this cell is
 * never registered in `CELL_REGISTRY`; it always receives its `column`.
 */
export function CustomColumnCell({
  flow,
  preOrder,
  column,
  disabled,
  onFlowUpdate,
}: Readonly<CustomColumnCellProps>) {
  const key = column?.customColumnKey;
  const isLocked = disabled || !isPreOrderModifiable(preOrder);

  const metadata = (flow.flow_metadata ?? {}) as Record<string, unknown>;
  const raw = key ? metadata[key] : undefined;
  const value =
    typeof raw === "number"
      ? raw
      : raw != null && raw !== "" && Number.isFinite(Number(raw))
        ? Number(raw)
        : null;

  // Persist the value back onto flow_metadata, preserving sibling keys
  // (other custom columns / custom fields share the same JSONB object).
  const onUpdate = useCallback(
    (flowId: string, updates: Record<string, unknown>) => {
      if (!key) return;
      onFlowUpdate(flowId, {
        flow_metadata: {
          ...flow.flow_metadata,
          [key]: updates[key] as number | null,
        },
      });
    },
    [onFlowUpdate, flow.flow_metadata, key],
  );

  const { localValue, handleChange, handleBlur } = useNumericCell(
    value,
    flow.id,
    key ?? "",
    onUpdate,
  );

  const blurTarget = useCallback(
    (target: HTMLInputElement) => target.blur(),
    [],
  );
  const handleKeyDown = useCellNavigation({ onBeforeNavigate: blurTarget });

  if (!key) return null;

  const control = (
    <input
      type="text"
      inputMode="decimal"
      data-testid={`flow-custom-${key}-input`}
      data-tab-cell
      value={localValue}
      onChange={(e) => handleChange(e.target.value)}
      onFocus={(e) => e.target.select()}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder="0"
      disabled={isLocked}
      className={cn(CELL_INPUT)}
    />
  );

  if (!isAiPendingCustomField(flow, key)) return control;

  // Custom columns share the flat `flow_metadata[key]` lane with custom fields,
  // so `AiCustomFieldBadge` resolves the pending "from → new" diff the same way.
  // Values are numeric, so the default number-aware formatter is used.
  return (
    <div className="flex min-w-0 flex-col">
      <div className={cn("flex min-w-0 items-center", AI_PENDING_CELL_CLASS)}>
        {control}
      </div>
      <AiCustomFieldBadge flow={flow} fieldKey={key} />
    </div>
  );
}
